import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  VictoryChart,
  VictoryTheme,
  VictoryBar,
  VictoryLine,
  VictoryAxis,
  VictoryLabel,
} from 'victory';
import { Box } from 'rebass';
import _ from 'lodash';
import { darken } from 'polished';

import { COLOR } from '../Theme';
import { getFormattedNumber, FORMAT_MODE } from '../Base/Number';

const GRID_COLOR = '#efefef';
const BAR_COLOR = '#ffb1b2';
const WIDTH_PER_CHAR = 10;
const Y_TICKS_OFFSET = 20;

const getYDomain = (data, valueType) => {
  const max = _.maxBy(data, valueType)[valueType] * 1.15;
  return [0, max];
};

const getMaxValueTextLength = (data = [], isPercent) => {
  if (isPercent) return 3.5;
  const valueStr =
    data.reduce(
      (prev, current) => (current.value && current.value > prev ? current.value : prev),
      0
    ) + '';
  return valueStr.length;
};

class Chart extends Component {
  static propTypes = {
    data: PropTypes.arrayOf(
      PropTypes.shape({
        range: PropTypes.shape({
          start: PropTypes.number,
          end: PropTypes.number,
        }),
        value: PropTypes.number,
        percent: PropTypes.number,
      })
    ),
    score: PropTypes.number,
    fullScore: PropTypes.number,
    // animationDuration: PropTypes.number,
    color: PropTypes.string,
    activeColor: PropTypes.string,
    containerHeight: PropTypes.number,
    isPercentValue: PropTypes.bool,
    xLabel: PropTypes.string,
    yLabel: PropTypes.string,
  };

  static defaultProps = {
    // animationDuration: 600,
    color: BAR_COLOR,
    containerHeight: 400,
    isPercentValue: true,
    xLabel: 'คะแนน',
    fullScore: 300,
  };

  render() {
    const {
      data,
      score,
      fullScore,
      color,
      activeColor,
      // animationDuration,
      containerHeight,
      isPercentValue,
      xLabel,
      yLabel,
    } = this.props;
    const valueType = isPercentValue ? 'percent' : 'value';
    const yDomain = getYDomain(data, valueType);
    const autoYLabel = yLabel ? yLabel : isPercentValue ? 'ร้อยละ' : 'จำนวน';

    const maxLength = getMaxValueTextLength(data, isPercentValue);
    const yLabelPadding = (maxLength * WIDTH_PER_CHAR || 50) + Y_TICKS_OFFSET;

    const formattedFullScore = Math.round(fullScore);

    return (
      <Box style={{ height: containerHeight }}>
        <VictoryChart
          theme={VictoryTheme.material}
          domain={{ x: [fullScore * -0.05, fullScore * 1.03], y: yDomain }}
          domainPadding={{ x: 0 }}
          height={350}
          width={600}
          padding={{ left: 70, top: score ? 60 : 20, bottom: 60, right: 20 }}
          // animate={{ duration: animationDuration }}

          /* possible solution if responsive problem occurs */
          // containerComponent={<VictoryContainer responsive={false} />}
        >
          <VictoryAxis
            dependentAxis
            label={autoYLabel}
            style={{
              axis: { stroke: 'none' },
              axisLabel: {
                padding: yLabelPadding,
                fontFamily: 'inherit',
              },
              ticks: { stroke: 'none' },
              tickLabels: {
                padding: Y_TICKS_OFFSET,
                fontFamily: 'inherit',
              },

              grid: { stroke: GRID_COLOR, strokeDasharray: 0 },
            }}
            tickFormat={x =>
              isPercentValue ? `${x}%` : getFormattedNumber(x, FORMAT_MODE.INTEGER)
            }
            tickCount={10}
          />
          <VictoryAxis
            label={xLabel}
            style={{
              axis: { strokeWidth: 1 },
              axisLabel: {
                padding: 32,
                fontFamily: 'inherit',
              },
              tickLabels: {
                fontFamily: 'inherit',
              },
              grid: { strokeWidth: 0 },
              fontFamily: 'inherit',
            }}
            tickValues={[...data.map(d => d.range.start), formattedFullScore]}
            tickFormat={t => `${Math.round(t)}`}
          />
          <VictoryBar
            barWidth={45}
            style={{
              data: {
                fill: ({ range: { start, end } }) => {
                  if (score > start && score <= end) {
                    return activeColor || darken(0.2, color);
                  }
                  return color;
                },
                strokeWidth: 0,
              },
            }}
            data={data}
            x="range.start"
            y={isPercentValue ? 'percent' : 'value'}
            alignment="start"
          />
          {score && [
            <VictoryLine
              key={'line1'}
              style={{ data: { stroke: 'none' } }}
              data={[{ x: score, y: yDomain[0] }, { x: score, y: yDomain[1] }]}
              labels={({ x, y }) => (y !== 0 ? x : null)}
              labelComponent={
                <VictoryLabel
                  renderInPortal
                  text={datum => (datum.y !== 0 ? `คะแนนที่ได้` : null)}
                  style={{
                    fontFamily: 'inherit',
                    fontSize: 14,
                    fill: COLOR.greyscale.grey1,
                  }}
                  dy={-18}
                />
              }
            />,
            <VictoryLine
              key={'line2'}
              style={{ data: { stroke: COLOR.greyscale.grey1 } }}
              data={[{ x: score, y: yDomain[0] }, { x: score, y: yDomain[1] }]}
              labels={({ x, y }) => (y !== 0 ? x : null)}
              labelComponent={
                <VictoryLabel
                  renderInPortal
                  text={datum => (datum.y !== 0 ? getFormattedNumber(datum.x) : null)}
                  style={{
                    fontFamily: 'inherit',
                    fontSize: 20,
                    fontWeight: 700,
                    fill: COLOR.greyscale.grey1,
                  }}
                  dy={5}
                />
              }
              // animate={false}
            />,
          ]}
        </VictoryChart>
      </Box>
    );
  }
}

export default Chart;
